import React, { useEffect, useRef, useState } from 'react';
import {
    Box, CircularProgress, IconButton, TextField, Typography,
} from '@mui/material';
import { ArrowUpward } from '@material-ui/icons';
import { CHAT_TYPE } from '../../../../utils/constants';
import { renderDqAiMarkdown } from '../utils/dqai_markdown';

/**
 * DQ AI preview chat panel — non-streaming.
 * White card bubbles with a "DQ" avatar for the assistant, brand-orange bubbles for
 * the tester, an animated typing indicator, and a blank state until a session starts.
 */
const DqAiChatPanel = ({
    classes, chatHistory, isSending, hasSession, onSendMessage,
}) => {
    const bottomRef = useRef(null);
    const [inputText, setInputText] = useState('');

    const handleSend = () => {
        const text = inputText.trim();
        if (!text || isSending || !onSendMessage) return;
        setInputText('');
        onSendMessage(text);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [chatHistory, isSending]);

    if (!hasSession) {
        return (
            <Box className={classes.emptyState}>
                <Box className={classes.emptyBadge}>DQ</Box>
                <Typography style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-text, #444)' }}>
                    DQ AI — Chatbot Preview
                </Typography>
                <Typography style={{ fontSize: 13, maxWidth: 340 }}>
                    Press <b>🔄 New chat</b> to start a session with a unique code,
                    or paste a session code above to resume a previous conversation.
                </Typography>
            </Box>
        );
    }

    return (
        <Box className={classes.chatPanel}>
            <Box className={classes.chatScroll}>
                {chatHistory.map((msg, idx) => (
                    msg.type === CHAT_TYPE.USER ? (
                        <Box key={idx} className={classes.userBubbleWrap}>
                            <Box className={classes.userBubble}>{msg.text}</Box>
                        </Box>
                    ) : (
                        <Box key={idx} className={classes.botBubbleWrap}>
                            <Box className={classes.botAvatar}>DQ</Box>
                            <Box className={classes.botBubble}>
                                <span dangerouslySetInnerHTML={{ __html: renderDqAiMarkdown(msg.text) }} />
                            </Box>
                        </Box>
                    )
                ))}

                {isSending && (
                    <Box className={classes.botBubbleWrap}>
                        <Box className={classes.botAvatar}>DQ</Box>
                        <Box className={classes.typingBubble}>
                            <span className={classes.typingDot} />
                            <span className={classes.typingDot} style={{ animationDelay: '0.2s' }} />
                            <span className={classes.typingDot} style={{ animationDelay: '0.4s' }} />
                        </Box>
                    </Box>
                )}

                <div ref={bottomRef} />
            </Box>

            {/* Borderless input + dark circular send button, matching the
                product chatbot (DQProV2 ChatSidebar) */}
            <Box sx={{
                borderTop: '1px solid var(--brand-border, #e0dcdc)', p: 1.5, display: 'flex', alignItems: 'flex-end', gap: 1,
            }}
            >
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder="Type a message…  (Enter to send, Shift+Enter for a new line)"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSending}
                    inputProps={{ maxLength: 4000 }}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiInputBase-root': { fontSize: 14, color: 'var(--brand-text, #333)' },
                    }}
                />
                <IconButton
                    onClick={handleSend}
                    disabled={isSending || !inputText.trim()}
                    sx={{
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        '&:hover': { bgcolor: '#333' },
                        '&.Mui-disabled': { bgcolor: '#ccc', color: '#fff' },
                    }}
                    size="small"
                >
                    {isSending
                        ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                        : <ArrowUpward style={{ fontSize: 22 }} />}
                </IconButton>
            </Box>
        </Box>
    );
};

export default DqAiChatPanel;
